"use client";
import { useState, useRef, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { usePathname } from "next/navigation";
import Logo from "./Logo";
import PremiumButton from "./PremiumButton";
import { navLinks } from "./data/navLinks";
import { motion, AnimatePresence } from "framer-motion";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef(null);
  const pathname = usePathname();
  const isDiscoveryPage = pathname === "/discovery";
  const isPricingPage = pathname === "/pricing";
  const isHomePage = pathname === "/";
  const isBlogPage = pathname.startsWith("/blog");

  // Handle scroll effect
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);

  // Close nav on outside click
  useEffect(() => {
    if (typeof document === 'undefined') return;
    
    const handleClickOutside = (e) => {
      if (menuOpen && navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <motion.header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
        scrolled || isBlogPage
          ? "bg-[var(--bg-primary)]/95 backdrop-blur-md py-4 border-b border-[var(--border-color)]"
          : "bg-transparent py-5 border-b border-transparent"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
        <Logo />

        {/* Desktop Nav - Show on all pages now */}
        <nav className="hidden md:flex items-center gap-4 lg:gap-6 text-sm">
          {navLinks.map((link) => {
            // If we're not on homepage and the link is a section anchor, redirect to main page
            const linkHref =
              !isHomePage && link.href.startsWith("#")
                ? `/${link.href}`
                : link.href;

            return (
              <a
                key={link.name}
                href={linkHref}
                className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)] hover:text-[var(--brand-color)] transition relative group"
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-[var(--brand-color)] transition-all duration-300 group-hover:w-full"></span>
              </a>
            );
          })}

          <PremiumButton
            href="/contact"
            size="sm"
            gaEvent={{
              action: 'cta_click',
              category: 'Header',
              label: 'Get in Touch - Header Desktop',
              parameters: {
                button_text: 'Get in Touch',
                button_location: 'header_desktop',
                destination_url: '/contact'
              }
            }}
          >
            Get in Touch
          </PremiumButton>
        </nav>

        {/* Show menu button on all pages now */}
        <button
          className="md:hidden text-[var(--text-primary)]"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Dropdown Menu - Show on all pages now */}
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            ref={navRef}
            className="md:hidden px-6 py-6 space-y-5 text-base bg-[var(--bg-secondary)]/95 backdrop-blur-md border-t border-[var(--border-color)] shadow-xl text-center absolute w-full"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {navLinks.map((link) => {
              // If we're not on homepage and the link is a section anchor, redirect to main page
              const linkHref =
                !isHomePage && link.href.startsWith("#")
                  ? `/${link.href}`
                  : link.href;

              return (
                <a
                  key={link.name}
                  href={linkHref}
                  className="block font-mono text-sm uppercase tracking-[0.18em] text-[var(--text-secondary)] hover:text-[var(--brand-color)] transition"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.name}
                </a>
              );
            })}

            <div className="flex justify-center pt-2">
              <PremiumButton
                href="/contact"
                size="md"
                onClick={() => setMenuOpen(false)}
                gaEvent={{
                  action: 'cta_click',
                  category: 'Header',
                  label: 'Get in Touch - Header Mobile',
                  parameters: {
                    button_text: 'Get in Touch',
                    button_location: 'header_mobile',
                    destination_url: '/contact'
                  }
                }}
              >
                Get in Touch
              </PremiumButton>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
